import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Download } from 'lucide-react';

const HEADINGS = [
  { id: 'two-divisions-one-leadership-team-no-shared-rhythm', label: 'Two divisions, one leadership team, no shared rhythm' },
  { id: 'what-the-framework-makes-visible', label: 'What the framework makes visible' },
  { id: 'four-steps', label: 'Four steps' },
  { id: 'decide-what-success-looks-like-before-you-start', label: 'Decide what success looks like before you start' },
];

export function ScenarioGlobalLogisticsPage() {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px' }
    );

    HEADINGS.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <main className="pt-32 pb-24 min-h-screen bg-white relative">
      <Helmet>
        <title>Transforming Global Logistics: An Illustrative Scenario | FIVE LENS Enneagram</title>
        <meta name="description" content="An illustrative scenario showing how the FIVE LENS framework can help a newly combined logistics leadership team work through departmental friction and slow decisions." />
        <meta property="og:title" content="Transforming Global Logistics: An Illustrative Scenario | FIVE LENS Enneagram" />
        <meta property="og:description" content="An illustrative scenario showing how the FIVE LENS framework can help a newly combined logistics leadership team work through departmental friction and slow decisions." />
        <link rel="canonical" href="https://www.fivelensenneagram.com/resource-library/transforming-global-logistics" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-16 items-start mt-8">
          <aside className="hidden lg:block w-72 shrink-0 sticky top-32 max-h-[calc(100vh-8rem)] overflow-y-auto scrollbar-hide pr-4">
            <div className="mb-4 text-xs font-bold uppercase tracking-widest text-ink/70">
              Contents
            </div>
            <nav className="flex flex-col gap-3 border-l-2 border-ground-deep">
              {HEADINGS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`text-left text-sm py-1 pl-4 border-l-2 -ml-[2px] transition-colors ${
                    activeId === id 
                      ? 'border-rule-3 text-ink font-bold' 
                      : 'border-transparent text-ink/70 hover:text-ink'
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
          </aside>

          <article className="flex-1 max-w-3xl min-w-0">
            <div className="mb-8">
              <Breadcrumb items={[
                { label: 'Resource Library', href: '/resource-library' },
                { label: 'Transforming Global Logistics' }
              ]} />
            </div>

            {/* Illustrative Scenario Top Badge */}
            <div className="mb-6 inline-block bg-ground-alt border border-ground-deep text-ink/80 text-xs font-semibold px-4 py-2 rounded-md">
              Illustrative scenario: a fictional composite, not a client case study.
            </div>

            <div className="prose prose-lg prose-headings:font-sans prose-h1:font-serif prose-h2:text-ink prose-h3:text-ink prose-h4:text-ink prose-a:text-rule-3 text-ink/70 article-content">

              {/* Hero Banner */}
              <div className="bg-gradient-to-br from-ink to-ink/80 text-white p-12 rounded-lg mb-16 relative overflow-hidden">
                <div>
                  <div className="mark text-white">FIVE LENS<span className="sub text-white/70">ENNEAGRAM</span></div>
                  <div className="tagline text-white/80">Developing People &amp; Expanding Possibilities</div>

                  <div className="kicker text-rule-3">Illustrative Scenario</div>
                  <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 mb-8">
                    <div>
                      <h1 className="!text-white !mb-4">Transforming Global<br />Logistics</h1>
                      <div className="deck !mb-0 max-w-2xl text-white/90">
                        An illustrative scenario showing how the FIVE LENS framework can help a newly combined logistics leadership team work through departmental friction and slow decisions.
                      </div>
                    </div>
                    <a 
                      href="/downloads/Transforming-Global-Logistics-Illustrative-Scenario.pdf" 
                      download 
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-rule-3 disabled:pointer-events-none disabled:opacity-50 border border-ground-deep bg-ground/10 hover:bg-ground/20 text-ground shadow-sm h-10 px-4 py-2 shrink-0 no-underline"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </a>
                  </div>
                  <div className="rule5">
                    <span style={{"background":"var(--rule-3)"}}></span>
                    <span style={{"background":"var(--rule-5)"}}></span>
                    <span style={{"background":"var(--rule-3)"}}></span>
                    <span style={{"background":"var(--rule-2)"}}></span>
                    <span style={{"background":"var(--rule-1)"}}></span>
                  </div>
                </div>
              </div>

              {/* Section: The Situation */}
              <section className="mb-16">
                <div className="border border-ground-deep border-t-4 border-t-ink bg-ground-alt/30 p-6 mb-8 rounded-b-md shadow-sm text-sm">
                  <p className="m-0"><b>This is an illustrative scenario, not a case study.</b> The company, people and events below are fictional. They are a composite of patterns that FIVE LENS practitioners commonly encounter in leadership teams going through integration and growth. No real client is described, and no outcomes are claimed. The purpose is to show how the framework is applied, so you can judge whether it fits your own context.</p>
                </div>

                <div className="border-b-2 border-ink pb-4 mb-8">
                  <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">The situation</div>
                  <h2 id="two-divisions-one-leadership-team-no-shared-rhythm">Two divisions, one leadership team, no shared rhythm</h2>
                </div>
                <p className="lead">Imagine a logistics business that has combined its freight-forwarding and warehousing divisions under one leadership team, to sell integrated services to large customers. Eighteen months later, the strategy still makes sense. Execution does not.</p>
                <p>The commercial director is winning integrated contracts, sometimes on terms operations has not seen. The operations director is protecting service levels and pushing back on every promise that adds risk. The finance director wants margins understood before anything launches. The new head of solutions, hired to design integrated services, spends most of their time brokering between the other three.</p>
                <p>The symptoms are familiar. New service lines take far longer to launch than planned. Escalations to the managing director are rising. Each function has built its own customer reporting. Two strong regional managers have left in the past year, both citing &quot;politics at the top&quot; in their exit interviews.</p>
                <p>Everyone on the leadership team is capable. Everyone agrees with the strategy. The problem is what happens between the agreement and the work.</p>
              </section>

              {/* Section: The Lenses */}
              <section className="mb-16">
                <div className="border-b-2 border-ink pb-4 mb-8">
                  <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">The lenses</div>
                  <h2 id="what-the-framework-makes-visible">What the framework makes visible</h2>
                </div>
                <p className="lead">In a scenario like this, a FIVE LENS practitioner would profile each member of the leadership team, then look at the patterns across the team as a whole.</p>

                <h3>The Enneagram: what each leader avoids</h3>
                <p>Suppose the profiles show the commercial director drawing heavily on the Achievement and Results pattern, which avoids failure and moves quickly toward visible wins. The operations director draws on Vigilance and Security, which avoids being caught out and plans for what could go wrong. The managing director leans on Harmony and Stability, which avoids conflict and smooths disagreements over. The finance director draws on the Pursuit of Quality, which avoids being wrong and holds out for the right answer.</p>
                <p>Put together, this explains a great deal. Commercial promises fast. Operations sees risk in the promise. Finance wants it done properly. And the managing director, whose instinct is to keep the peace, lets each of them leave the meeting believing they won. The decision is made three times, in three different ways.</p>

                <h3>Social Drives: which needs the integration threatened</h3>
                <p>The integration changed who reports to whom and whose expertise counts. The warehousing leaders, now inside a larger group, feel their standing has dropped, an esteem need under threat. Middle managers from both divisions are unsure whose processes will survive, a security need. Under threat, energy contracts into territory, and the separate customer reports are the visible result.</p>

                <h3>Energy Centres: how decisions are being made</h3>
                <p>Commercial is action-led. Finance is analysis-led. Operations is split between the two. Nobody on the team is paying much attention to the emotional centre, which is why the departure of two respected regional managers came as a surprise to the leadership team and to nobody else.</p>
              </section>

              {/* Section: What a practitioner would do */}
              <section className="mb-16">
                <div className="border-b-2 border-ink pb-4 mb-8">
                  <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">What a practitioner would do</div>
                  <h2 id="four-steps">Four steps</h2>
                </div>
                <ol className="space-y-4 my-6 list-decimal list-inside pl-2">
                  <li className="pl-2"><b>Individual debriefs first.</b> Each leader receives their own profile privately before anything is shared. Senior people are more open in a group once they have had time to recognise themselves in the results.</li>
                  <li className="pl-2"><b>A Team Discovery Day.</b> The leadership team sees its combined map. The most useful moment is often when the managing director sees that keeping the peace has been leaving decisions unmade, and the others see that their colleagues&apos; resistance is self-protection rather than obstruction.</li>
                  <li className="pl-2"><b>Three practical agreements.</b> Using that shared language, the team might agree named decision owners for new service launches, a rule that no integrated contract is signed without an operations review, and two shared goals that no single function can deliver alone, reviewed before functional updates at every leadership meeting.</li>
                  <li className="pl-2"><b>A Team Performance Process.</b> Over the following months, the practitioner works with the team on the specific friction points: how to disagree openly in the room, how to make a decision stick, and how to bring the emotional centre back into the conversation.</li>
                </ol>
              </section>

              {/* Section: What to measure */}
              <section className="mb-16">
                <div className="border-b-2 border-ink pb-4 mb-8">
                  <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">What to measure</div>
                  <h2 id="decide-what-success-looks-like-before-you-start">Decide what success looks like before you start</h2>
                </div>
                <p className="lead">Because this is a scenario, we report no results. If you ran something similar, these are indicators worth baselining first.</p>

                <div className="overflow-x-auto my-6">
                  <table className="min-w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="border-b-2 border-ink">
                        <th className="py-2 pr-4 font-bold">Indicator</th>
                        <th className="py-2 font-bold">Why it matters</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-ground-deep"><td className="py-3 pr-4 font-bold align-top">Time from approved idea to launch</td><td className="py-3 align-top">The clearest measure of whether the leadership team can turn agreement into action across functions.</td></tr>
                      <tr className="border-b border-ground-deep"><td className="py-3 pr-4 font-bold align-top">Escalations to the managing director</td><td className="py-3 align-top">Fewer escalations suggests functions are resolving issues with each other directly.</td></tr>
                      <tr className="border-b border-ground-deep"><td className="py-3 pr-4 font-bold align-top">Decisions reopened</td><td className="py-3 align-top">Count how many leadership decisions are revisited without new information. A falling count means decisions are sticking.</td></tr>
                      <tr className="border-b border-ground-deep"><td className="py-3 pr-4 font-bold align-top">Regretted management leavers</td><td className="py-3 align-top">A lagging indicator. Exit interview themes are as useful as the numbers.</td></tr>
                      <tr className="border-b border-ground-deep"><td className="py-3 pr-4 font-bold align-top">Two-levels-down priority check</td><td className="py-3 align-top">Ask a sample of managers to name the top three priorities. Track how closely their lists match each other.</td></tr>
                    </tbody>
                  </table>
                </div>

                <h3>Questions for your own leadership team</h3>
                <ul className="space-y-2 my-4 list-disc list-inside">
                  <li>When a decision is made in your leadership meeting, does everyone leave with the same understanding of it?</li>
                  <li>Who on your team keeps the peace, and what does that cost in unmade decisions?</li>
                  <li>Since your last reorganisation, whose standing or security has been most threatened?</li>
                  <li>Which of the three centres is missing from your team&apos;s decisions?</li>
                </ul>
              </section>

              {/* Call to action footer */}
              <div className="border border-ground-deep border-t-4 border-t-ink bg-ground-alt/30 p-8 my-12 rounded-b-md shadow-sm">
                <h3 className="!mt-0">Download the full scenario</h3>
                <p className="mb-6 text-sm">
                  Get the complete printable PDF scenario including supply chain team mapping templates.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="/downloads/Transforming-Global-Logistics-Illustrative-Scenario.pdf" 
                    download 
                    className="inline-flex items-center justify-center px-6 py-3 bg-ink text-white font-bold text-xs uppercase tracking-widest hover:bg-rule-3 transition-colors no-underline"
                  >
                    <Download className="mr-2 h-4 w-4" /> Download Scenario PDF
                  </a>
                </div>
              </div>

            </div>
          </article>
        </div>
      </div>
    </main>
  );
}
